import { reactive } from '@vue/composition-api'
import { get } from 'lodash'
import TableGroupApi from '@/api/waiter/TableGroup'
import { findParentGroup, findChildGroup } from '@/utils/tableGroupColors'

// 卓の連結(卓グループ)のローカル状態・HallChannel同期・操作をホール画面/フロアマップで共有する。
// グループは hall.attributes.tableGroups.data(JSONAPI)に置き、サーバの broadcast で全端末を同期する。
export default function useTableGroups({ halls, vm, currentUserId }) {
  const tableGroupsOf = hall => get(hall, 'attributes.tableGroups.data', [])

  const tableNameOf = (hall, tableNumber) => {
    const attrs = hall?.attributes || {}
    const custom = (attrs.tableLayouts || []).find(l => Number(l.table_number) === Number(tableNumber))?.name

    return custom || `${attrs.tablePrefix || ''}${tableNumber}${attrs.tableSuffix || '卓'}`
  }
  const tableNamesOf = (hall, tableNumbers) => (tableNumbers || []).map(n => tableNameOf(hall, n)).join('・')

  const groupsListOf = hall => {
    if (!hall.attributes.tableGroups) vm.$set(hall.attributes, 'tableGroups', { data: [] })
    if (!hall.attributes.tableGroups.data) vm.$set(hall.attributes.tableGroups, 'data', [])

    return hall.attributes.tableGroups.data
  }

  const upsertTableGroup = (hallIdx, group) => {
    const hall = halls.value[hallIdx]
    if (!hall?.attributes || !group) return
    const list = groupsListOf(hall)
    const idx = list.findIndex(g => String(g.id) === String(group.id))
    if (idx === -1) list.push(group)
    else list.splice(idx, 1, group)
  }

  const removeTableGroup = (hallIdx, groupId) => {
    const hall = halls.value[hallIdx]
    if (!hall?.attributes) return
    const list = groupsListOf(hall)
    const idx = list.findIndex(g => String(g.id) === String(groupId))
    if (idx !== -1) list.splice(idx, 1)
  }

  // 自動解除(会計完了/削除/別ホール移動)は操作した端末を含む全端末に知らせる。
  // 手動解除は操作した端末側で成功トーストを出すので、他端末にだけ知らせる
  const notifyReleased = ({
    hallIdx, parentTableNumber, tableNumbers, reason, fromUserId,
  }) => {
    const hall = halls.value[hallIdx]
    if (!hall) return
    const parent = tableNameOf(hall, parentTableNumber)
    const children = tableNamesOf(hall, tableNumbers)

    switch (reason) {
      case 'checkout':
        vm.$toast.info(`${parent}の会計が完了したため、${children}との連結を解除しました`)
        break
      case 'destroy':
        vm.$toast.info(`${parent}を削除したため、${children}との連結を解除しました`)
        break
      case 'parent_moved':
        vm.$toast.info(`${parent}を別のホールへ移動したため、${children}との連結を解除しました`)
        break
      default:
        if (fromUserId !== currentUserId) vm.$toast.info(`${parent}の連結が解除されました(${children})`)
        break
    }
  }

  // HallChannel の受信ハンドラから呼ぶ。対象メッセージなら true
  const handleChannelMessage = payload => {
    const { strategy, hallIdx } = payload
    if (strategy === 'upsertTableGroup') {
      upsertTableGroup(hallIdx, payload.tableGroup)

      return true
    }
    if (strategy === 'releaseTableGroup') {
      removeTableGroup(hallIdx, payload.tableGroupId)
      notifyReleased(payload)

      return true
    }

    return false
  }

  // ── 操作(ダイアログ・解除) ──
  const dialog = reactive({
    show: false,
    hallIdx: null,
    table: null,
    hall: null,
    group: null,
  })

  const openTableGroup = (hallIdx, table) => {
    const hall = halls.value[hallIdx]
    if (!hall || !table) return
    dialog.hallIdx = hallIdx
    dialog.table = table
    dialog.hall = hall
    dialog.group = findParentGroup(tableGroupsOf(hall), table.attributes.tableNumber)
    dialog.show = true
  }

  const onTableGroupSaved = group => {
    upsertTableGroup(dialog.hallIdx, group)
    vm.$toast.success('卓を連結しました')
  }

  const onTableGroupReleased = group => {
    removeTableGroup(dialog.hallIdx, group.id)
    vm.$toast.success('連結を解除しました')
  }

  const releaseTableGroup = async (hallIdx, table) => {
    const hall = halls.value[hallIdx]
    const group = findParentGroup(tableGroupsOf(hall), table?.attributes?.tableNumber)
    if (!group) return
    try {
      await TableGroupApi.deleteTableGroup(table.id)
      removeTableGroup(hallIdx, group.id)
      vm.$toast.success('連結を解除しました')
    } catch (e) {
      vm.$toast.error(e?.response?.data?.message || '連結の解除に失敗しました')
    }
  }

  // 子卓側から自分だけ外す。最後の1卓ならグループごと解除される
  const unlinkTableGroup = async (hallIdx, tableNumber) => {
    const hall = halls.value[hallIdx]
    const group = findChildGroup(tableGroupsOf(hall), tableNumber)
    if (!group) return
    try {
      const res = await TableGroupApi.removeTableGroupMember(group.attributes.parentTableId, tableNumber)
      if (res?.data?.released) removeTableGroup(hallIdx, group.id)
      else if (res?.data?.data) upsertTableGroup(hallIdx, res.data.data)
      vm.$toast.success('連結を解除しました')
    } catch (e) {
      vm.$toast.error(e?.response?.data?.message || '連結の解除に失敗しました')
    }
  }

  return {
    tableGroupsOf,
    tableNameOf,
    tableNamesOf,
    findParentGroup,
    findChildGroup,
    upsertTableGroup,
    removeTableGroup,
    handleChannelMessage,
    tableGroupDialog: dialog,
    openTableGroup,
    onTableGroupSaved,
    onTableGroupReleased,
    releaseTableGroup,
    unlinkTableGroup,
  }
}
